import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {BaseItemMultiplyColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-multiply-color-option';
import type {RGBA} from '@Utils/color.util';
import {BaseItemMultiplyOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-multiply-opacity-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';

interface BaseMultiplyAdjustmentOptionProps {
  panelId: string;
  multiply: boolean;
  multiplyColor: RGBA;
  multiplyOpacity: number;

  onToggleMultiply(): void;

  onClickColorFill(): void;

  updateOpacity(multiplyOpacity: number, undoable?: boolean): void;
}

export function BaseMultiplyAdjustmentOption({...props}: BaseMultiplyAdjustmentOptionProps): ReactElement | null {
  useEffect((): void => {
    if (props.multiply) {
      updateHeight('228px');
      return;
    }
    updateHeight('112px');
  }, [props.multiply]);

  const updateHeight = (height: string): void => {
    updateOptionsPanelHeight(props.panelId, height);
  };

  const getMultiplyColorOption = (): ReactElement => {
    return (
      <BaseItemMultiplyColorOption
        multiplyColor={props.multiplyColor}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
        className="spacing-m-b-3"
      />
    );
  };

  const getMultiplyOpacityOption = (): ReactElement => {
    return (
      <BaseItemMultiplyOpacityOption
        multiplyOpacity={props.multiplyOpacity}
        updateOpacity={(value: number, undoable = true): void => {
          props.updateOpacity(value, undoable);
        }}
      />
    );
  };

  const getMultiplyColorAndOpacity = (): ReactElement | null => {
    if (props.multiply) {
      return (
        <>
          {getMultiplyColorOption()}
          {getMultiplyOpacityOption()}
        </>
      );
    }

    return null;
  };

  const getContent = (): ReactElement => {
    return (
      <div>
        {getMultiplyColorAndOpacity()}
        <ContextualMenuToggleOption
          id="multiply-adjustment-option"
          label={window.i18next.t('pmwjs_multiply')}
          className={props.multiply ? 'spacing-m-t-3' : ''}
          isChecked={props.multiply}
          onClick={(): void => {
            props.onToggleMultiply();
          }}
        />
      </div>
    );
  };

  return getContent();
}
